import { Card } from "@shared/components/ui/card";
import { format } from "date-fns";
import {
  AlertCircle,
  CalendarIcon,
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Package,
  Phone,
  Store,
  Truck,
  User,
  UserCheck,
  UserPlus,
} from "lucide-react";
import { Separator } from "@shared/components/ui/separator";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AssignCollectorToPickupDialog from "./AssignCollectorToPickupDialog";

const statusColors = {
  pending: "bg-gray-100 text-gray-800 border-gray-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  picked_up: "bg-purple-100 text-purple-800 border-purple-200",
  in_transit: "bg-blue-100 text-blue-800 border-blue-200",
  signed: "bg-teal-100 text-teal-800 border-teal-200",
  failed: "bg-orange-100 text-orange-800 border-orange-200",
};

const statusIcons = {
  signed: <CheckCircle2 className="h-4 w-4 text-teal-500" />,
  approved: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  in_transit: <Truck className="h-4 w-4 text-blue-500" />,
  picked_up: <Package className="h-4 w-4 text-purple-500" />,
  pending: <Clock className="h-4 w-4 text-gray-500" />,
  rejected: <AlertCircle className="h-4 w-4 text-red-500" />,
  failed: <AlertCircle className="h-4 w-4 text-orange-500" />,
};

const PickupCardForLivreurManagement = ({ pickupData, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();

  return (
    <Card className="w-full shadow-md transition-transform duration-300 hover:bg-background/40 hover:scale-[0.99]">
      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-3">
            {/* Header Section */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <h3 className="text-sm font-semibold truncate">
                  {pickupData.address} - {pickupData.pickupCity}
                </h3>
              </div>
              <Badge
                variant="outline"
                className={
                  statusColors[pickupData.status] || statusColors.pending
                }
              >
                <div className="mr-1">{statusIcons[pickupData.status]}</div>
                {t(pickupData.status?.replace(/_/g, " ") || "Unknown")}
              </Badge>
            </div>

            {/* Pickup Details Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Phone */}
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium truncate">
                  {pickupData.phone}
                </span>
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm font-medium">
                  {pickupData.quantity} {t("items")}
                </span>
              </div>

              {/* Client Info (Admin/Manager view) */}
              {(auth.isAdmin || auth.isManager) && pickupData.user && (
                <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-md col-span-2">
                  <Store className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {pickupData.user.brandName || pickupData.user.fullName}
                  </span>
                </div>
              )}

              {/* Product Info */}
              {pickupData.product && (
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md col-span-2">
                  <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm truncate">
                    {pickupData.product.name}
                  </span>
                </div>
              )}
            </div>

            {/* Notes */}
            {pickupData.note && (
              <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded">
                <span className="font-medium">{t("Note")}:</span>{" "}
                {pickupData.note}
              </div>
            )}

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center text-xs text-muted-foreground bg-muted/50 rounded-md p-1.5">
                <CalendarIcon className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                <span className="truncate">
                  {format(new Date(pickupData.createdAt), "dd/MM/yyyy HH:mm")}
                </span>
              </div>
              {pickupData.assignedAt && (
                <div className="flex items-center text-xs text-muted-foreground bg-green-50 dark:bg-green-950/20 rounded-md p-1.5">
                  <UserCheck className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                  <span className="truncate">
                    {format(
                      new Date(pickupData.assignedAt),
                      "dd/MM/yyyy HH:mm"
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Collector & Actions */}
          <div className="space-y-3">
            {/* Collector Assignment Status */}
            <div>
              {pickupData.collector ? (
                <div className="flex flex-col gap-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-md border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-900 dark:text-green-100 truncate">
                        {pickupData.collector.fullName ||
                          pickupData.collector.email}
                      </p>
                      {pickupData.collector.phone && (
                        <p className="text-xs text-green-700 dark:text-green-300">
                          {pickupData.collector.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  {pickupData.collectorPickupPrice > 0 && (
                    <Badge
                      variant="outline"
                      className="bg-green-50 border-green-300 w-fit"
                    >
                      {pickupData.collectorPickupPrice} DH
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2 bg-orange-50 dark:bg-orange-950/20 rounded-md border border-orange-200 dark:border-orange-800">
                  <User className="h-4 w-4 text-orange-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-orange-900 dark:text-orange-100">
                    {t("Not assigned")}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/pickups/${pickupData._id}`)}
                className="w-full"
              >
                <Eye className="h-4 w-4 mr-1" />
                {t("View")}
              </Button>

              {(auth.isAdmin || auth.isManager) && (
                <AssignCollectorToPickupDialog
                  pickupData={pickupData}
                  onSuccess={onSuccess}
                  trigger={
                    <Button
                      size="sm"
                      variant={pickupData.collector ? "outline" : "default"}
                      className="w-full"
                    >
                      {pickupData.collector ? (
                        <>
                          <UserCheck className="h-4 w-4 mr-1" />
                          {t("Reassign")}
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-1" />
                          {t("Assign")}
                        </>
                      )}
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PickupCardForLivreurManagement;
